import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import {
	graphql,
	readInlineData,
	useLazyLoadQuery,
	useRefetchableFragment,
} from "react-relay";
import z from "zod";
import type { tags_tableFragment$key } from "@/components/tables/__generated__/tags_tableFragment.graphql";
import { TagsTable, tagTableFragment } from "@/components/tables/tags";
import type { tagsPageFragment$key } from "./__generated__/tagsPageFragment.graphql";
import type { tagsQuery } from "./__generated__/tagsQuery.graphql";
import type { tagsTableQuery } from "./__generated__/tagsTableQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/audiencia/tags",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({
		from: Route.fullPath,
	});
	const data = useLazyLoadQuery<tagsQuery>(
		graphql`
			query tagsQuery($slug: String!, $first: Int, $last: Int, $after: String, $before: String) {
				club(slug: $slug) {
					...tagsPageFragment @arguments(first: $first, last: $last, after: $after, before: $before)
				}
			}
		`,
		{
			slug: club?.slug,
			first: 15,
			after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
		},
	);

	const [tags, refetch] = useRefetchableFragment<
		tagsTableQuery,
		tagsPageFragment$key
	>(
		graphql`
			fragment tagsPageFragment on Club
				@refetchable(queryName: "tagsTableQuery")
				@argumentDefinitions(
					first: { type: "Int" }
					last: { type: "Int" }
					after: { type: "String" }
					before: { type: "String" }
				) {
				tags(first: $first, after: $after, before: $before, last: $last) {
					edges {
						node {
							...tags_tableFragment
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
						startCursor
						endCursor
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	const tagsData =
		tags?.tags?.edges
			.filter(Boolean)
			.map(({ node }) =>
				readInlineData<tags_tableFragment$key>(tagTableFragment, node),
			) || [];
	const refreshTags = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	return (
		<div className="p-6">
			<TagsTable
				data={tagsData}
				clubSlug={club?.slug ?? ""}
				onChanged={refreshTags}
				isPending={isPending}
				totalCount={tags?.tags.pageInfo.totalCount || 0}
				hasNextPage={Boolean(tags?.tags.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(tags?.tags.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					tags?.tags.edges[0]
						? cursorToOffset(tags?.tags.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					tags?.tags.edges[tags?.tags.edges.length - 1]
						? cursorToOffset(
								tags?.tags.edges[tags?.tags.edges.length - 1].cursor,
							) + 1
						: 1
				}
			/>
		</div>
	);
}
